/**
 * Workspace list — v2 Knowledge Workspaces route (Phase 3.1).
 *
 * Presentation of the existing `/document-sets` list under the v2 name. Uses
 * `useWorkspaces` (thin re-export of `useDocumentSets`) so the DB story is
 * unchanged. The old `/document-sets` route continues to work until Phase 6
 * drops it.
 */

import { fork } from '@cortex/utils/fork';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { Table, type IColumn } from '../../components/common/Table';
import { useDetachAllSkills } from '../../hooks/useSkills';
import { useCreateWorkspace, useDeleteWorkspace, useWorkspaces } from '../../hooks/useWorkspaces';
import { formatRelative } from '../../lib/format';
import type { IDocumentSet } from '../../lib/metaphysics';

export const Route = createFileRoute('/workspaces/')({ component: WorkspacesListPage });

function WorkspacesListPage() {
  const list = useWorkspaces();
  const create = useCreateWorkspace();
  const deleteWorkspace = useDeleteWorkspace();
  const detachAll = useDetachAllSkills();
  const nav = useNavigate();
  const [openCreate, setOpenCreate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pendingDelete, setPendingDelete] = useState<IDocumentSet | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  function reset() {
    setName('');
    setDescription('');
    create.reset();
  }

  async function submit() {
    if (!name.trim()) return;
    try {
      const created = await create.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      reset();
      setOpenCreate(false);
      await nav({ to: '/workspaces/$id', params: { id: created.id } });
    } catch (err) {
      console.error('workspaces/create', err);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      // Best-effort: clean up cortex-api skill attachments first. Orphaned
      // attachments are harmless (invisible in reads), so a failure here
      // must not block the workspace delete.
      try {
        await detachAll.mutateAsync(pendingDelete.id);
      } catch (err) {
        console.warn('workspaces/detachAllSkills failed (continuing with delete)', err);
      }
      await deleteWorkspace.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
    } catch (err) {
      setDeleteError((err as Error).message);
    } finally {
      setDeleting(false);
    }
  }

  const cols: IColumn<IDocumentSet>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (w) => (
        <Link
          to="/workspaces/$id"
          params={{ id: w.id }}
          style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-med)' }}
        >
          {w.name}
        </Link>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      cell: (w) => (
        <span className="t-secondary">{w.description ?? <span className="t-muted">—</span>}</span>
      ),
    },
    {
      key: 'sources',
      header: 'Sources',
      mono: true,
      width: 80,
      cell: (w) =>
        w.sourceCount != null ? String(w.sourceCount) : <span className="t-muted">—</span>,
    },
    {
      key: 'artifacts',
      header: 'Artifacts',
      mono: true,
      width: 80,
      cell: (w) =>
        w.artifactCount != null ? String(w.artifactCount) : <span className="t-muted">—</span>,
    },
    {
      key: 'kg',
      header: 'KG',
      mono: true,
      width: 80,
      cell: (w) =>
        w.latestKgVersion != null ? (
          <span className="badge badge--success" style={{ fontSize: 10 }}>
            v{w.latestKgVersion}
          </span>
        ) : (
          <span className="t-muted">—</span>
        ),
    },
    {
      key: 'updated',
      header: 'Updated',
      mono: true,
      cell: (w) => formatRelative(w.updatedAt),
      width: 140,
    },
    {
      key: 'actions',
      header: '',
      width: 80,
      cell: (w) => (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={(e) => {
            e.stopPropagation();
            setDeleteError(null);
            setPendingDelete(w);
          }}
          title={`Delete ${w.name}`}
        >
          Delete
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Knowledge bases"
        actions={
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setOpenCreate(true)}
          >
            New knowledge base
          </button>
        }
      />
      <Table<IDocumentSet>
        rows={list.data}
        columns={cols}
        rowKey={(w) => w.id}
        isLoading={list.isLoading}
        error={list.error as Error | null}
        emptyTitle="No knowledge bases yet"
        emptyDescription="Create a knowledge base to upload sources and build a knowledge graph."
        emptyCta={
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setOpenCreate(true)}
          >
            New knowledge base
          </button>
        }
        onRowClick={async (w) => {
          try {
            await nav({ to: '/workspaces/$id', params: { id: w.id } });
          } catch (err) {
            console.error(err);
          }
        }}
      />

      <Modal
        open={openCreate}
        onClose={() => {
          setOpenCreate(false);
          reset();
        }}
        title="New knowledge base"
        eyebrow="KNOWLEDGE BASE · NEW"
        footer={
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setOpenCreate(false);
                reset();
              }}
              disabled={create.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => {
                fork(submit());
              }}
              disabled={create.isPending || !name.trim()}
            >
              {create.isPending ? 'Creating…' : 'Create'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <label className="field">
            <span className="field-label">Name</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus={true}
              required={true}
              placeholder="SOP corpus"
            />
          </label>
          <label className="field">
            <span className="field-label">Description (optional)</span>
            <textarea
              className="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What lives in this knowledge base?"
            />
          </label>
          {create.error ? (
            <div className="alert alert--danger">
              Failed to create: {(create.error as Error).message}
            </div>
          ) : null}
        </div>
      </Modal>

      <Modal
        open={pendingDelete !== null}
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
        title={`Delete ${pendingDelete?.name ?? 'knowledge base'}?`}
        eyebrow="KNOWLEDGE BASE · DELETE"
        footer={
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setPendingDelete(null)}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--danger btn--sm"
              onClick={() => {
                fork(confirmDelete());
              }}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete knowledge base'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <p className="t-secondary" style={{ margin: 0 }}>
            This permanently deletes the knowledge base and removes its skill attachments. Committed
            content versions, KG versions, and LightRAG data become unreachable.
          </p>
          <div className="alert alert--warning" style={{ fontSize: 12 }}>
            This cannot be undone.
          </div>
          {deleteError ? (
            <div className="alert alert--danger">Failed to delete: {deleteError}</div>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
